import { useEffect } from 'react';

import { useBxpWebStargateClient } from '@wac-web/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client';

import {
	useFastshiftDispatch,
	useFastshiftSelector,
	FastshiftDispatchPromise,
	TABLE_IDS,
} from '@/state';
import * as StateActions from '@/state/actions';

/**
 * Fetches support requests data with deduplication and abort cleanup.
 * Prevents redundant API calls by checking lastFetchParams.
 */
export const useFetchSupportRequests = () => {
	const dispatch = useFastshiftDispatch();
	const stargateClient = useBxpWebStargateClient();
	const { data: customerDomainData } = useFastshiftSelector((state) => state.customerDomain);

	const { lastFetchParams } = useFastshiftSelector((state) => state.supportRequests);

	useEffect(() => {
		let supportRequestsPromise: FastshiftDispatchPromise<unknown> | null = null;

		if (customerDomainData) {
			const needsFetch =
				!lastFetchParams ||
				lastFetchParams.customerDomainId !== customerDomainData.customerDomainId ||
				lastFetchParams.queryParams.orgId !== undefined ||
				lastFetchParams.queryParams.permissionLevel !== undefined;

			if (!needsFetch) {
				return;
			}

			// Clear migration tickets table controls when fetching new domain-wide data.
			dispatch(
				StateActions.clearTableControls({
					tableId: TABLE_IDS.MIGRATION_TICKETS,
				}),
			);

			supportRequestsPromise = dispatch(
				StateActions.getFastshiftSupportRequests({
					stargateClient,
					customerDomainId: customerDomainData.customerDomainId,
					queryParams: {},
				}),
			);
		}

		return () => {
			if (supportRequestsPromise) {
				supportRequestsPromise.abort();
			}
		};
	}, [dispatch, stargateClient, customerDomainData, lastFetchParams]);
};
